import { CURRENCIES, type Currency } from '@fos/shared/contracts/common';
import {
  BILLING_CYCLES,
  type BillingCycle,
  createServiceInput,
  SERVICE_KINDS,
  SERVICE_STATUSES,
  type ServiceDto,
  type ServiceKind,
  type ServiceStatus,
  updateServiceInput,
} from '@fos/shared/contracts/services';
import { BILLING_CYCLE_LABELS, SERVICE_KIND_LABELS, SERVICE_STATUS_LABELS } from '@fos/shared/labels';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field, Label } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Select } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import {
  amountInputValue,
  hoursInputValue,
  issuesToErrors,
  parseAmountInput,
  parseHoursInput,
  zodIssues,
} from '../../maintenance/form-utils';
import { FULLSCREEN_ON_PHONE } from '../../mobile';

const FIELD_KEYS = [
  'name',
  'kind',
  'status',
  'price',
  'currency',
  'cycle',
  'periodStart',
  'periodEnd',
  'nextPaymentDate',
  'descriptionMd',
  'billingTerms',
  'hoursPoolSeconds',
  'assetIds',
  'autoClientReminder',
] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

interface FormState {
  name: string;
  kind: ServiceKind;
  status: ServiceStatus;
  price: string;
  currency: Currency;
  cycle: BillingCycle;
  periodStart: string;
  periodEnd: string;
  nextPaymentDate: string;
  descriptionMd: string;
  billingTerms: string;
  hours: string;
  assetIds: string[];
  remindOwner: boolean;
  remindClient: boolean;
  remindOverdue: boolean;
  autoClientReminder: boolean;
}

function initial(service: ServiceDto | undefined, defaults: { kind: ServiceKind; assetIds: string[] }): FormState {
  return {
    name: service?.name ?? '',
    kind: service?.kind ?? defaults.kind,
    status: service?.status ?? 'active',
    price: amountInputValue(service?.price ?? null),
    currency: ((service?.currency as Currency | undefined) ?? 'PLN') as Currency,
    cycle: service?.cycle ?? (defaults.kind === 'maintenance' ? 'year' : 'none'),
    periodStart: service?.periodStart ?? '',
    periodEnd: service?.periodEnd ?? '',
    nextPaymentDate: service?.nextPaymentDate ?? '',
    descriptionMd: service?.descriptionMd ?? '',
    billingTerms: service?.billingTerms ?? '',
    hours: hoursInputValue(service?.hoursPoolSeconds),
    assetIds: service ? service.assets.map((a) => a.id) : defaults.assetIds,
    remindOwner: true,
    remindClient: true,
    remindOverdue: true,
    autoClientReminder: service?.autoClientReminder ?? false,
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);

/**
 * Formularz umowy utrzymania / usługi (modal 560 px; widok bez osobnego ekranu w Figmie — wzorce modali
 * „Dodaj zasób” i „Nowy projekt”). Pola: nazwa, rodzaj, cena netto z cyklem (tylko z „Widzi stawki i kwoty”),
 * okres, następna płatność, ustalenia (Markdown), warunki, pula godzin, objęte zasoby, przypomnienia (nowa
 * umowa utrzymania) i przełącznik „wysyłaj automatycznie” (wymaga „Wysyła maile”; na razie tylko zapisywany —
 * przypomnienie i tak trafia do Akceptacji).
 */
export function ServiceFormDialog({
  open,
  onOpenChange,
  clientId,
  service,
  defaultKind = 'maintenance',
  defaultAssetIds = [],
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** klient usługi (przy edycji brany z usługi) */
  clientId: string;
  /** edycja; brak = nowa usługa */
  service?: ServiceDto;
  defaultKind?: ServiceKind;
  /** zasoby zaznaczone przy nowej umowie (np. z karty zasobu) */
  defaultAssetIds?: string[];
  onSaved?: (service: ServiceDto) => void;
}) {
  const access = useAccess();
  const utils = trpc.useUtils();
  const editing = Boolean(service);
  const seesRates = access.flag('seesRates');
  const sendsEmails = access.flag('sendsEmails');
  const canAssets = access.can('maintenance', 'edit');
  const assetsQuery = trpc.maintenance.listAssets.useQuery({ clientId }, { enabled: open && canAssets });
  const create = trpc.services.create.useMutation();
  const update = trpc.services.update.useMutation();

  const [form, setForm] = useState<FormState>(() => initial(service, { kind: defaultKind, assetIds: defaultAssetIds }));
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setForm(initial(service, { kind: defaultKind, assetIds: defaultAssetIds }));
    setErrors({});
    setFormError(null);
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const kindOptions = SERVICE_KINDS.filter(
    (k) => k === service?.kind || access.can(k === 'maintenance' ? 'maintenance' : 'projects', 'edit'),
  ).map((k) => ({ value: k, label: SERVICE_KIND_LABELS[k] }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const hours = parseHoursInput(form.hours);
    if (hours === undefined) {
      setErrors({ hoursPoolSeconds: 'Podaj liczbę godzin, np. 3 albo 1,5' });
      return;
    }
    let price: string | null | undefined;
    if (seesRates) {
      price = parseAmountInput(form.price);
      if (price === undefined) {
        setErrors({ price: 'Podaj kwotę, np. 1990 albo 1 990,50' });
        return;
      }
    }
    const fields = {
      name: form.name,
      kind: form.kind,
      ...(seesRates ? { price, currency: form.currency } : {}),
      cycle: form.cycle,
      periodStart: orNull(form.periodStart),
      periodEnd: orNull(form.periodEnd),
      nextPaymentDate: orNull(form.nextPaymentDate),
      descriptionMd: orNull(form.descriptionMd),
      billingTerms: orNull(form.billingTerms),
      hoursPoolSeconds: hours,
      autoClientReminder: form.autoClientReminder,
      ...(canAssets ? { assetIds: form.assetIds } : {}),
    };
    try {
      let saved: ServiceDto;
      if (service) {
        const input = { ...fields, status: form.status, id: service.id, version: service.version };
        const parsed = updateServiceInput.safeParse(input);
        if (!parsed.success) {
          setErrors(issuesToErrors(zodIssues(parsed.error), FIELD_KEYS));
          return;
        }
        saved = await update.mutateAsync(input);
        toast.success('Zapisano umowę');
      } else {
        const input = {
          ...fields,
          clientId,
          status: form.status,
          ...(form.kind === 'maintenance'
            ? { reminders: { owner: form.remindOwner, client: form.remindClient, overdue: form.remindOverdue } }
            : {}),
        };
        const parsed = createServiceInput.safeParse(input);
        if (!parsed.success) {
          setErrors(issuesToErrors(zodIssues(parsed.error), FIELD_KEYS));
          return;
        }
        saved = await create.mutateAsync(input);
        toast.success(form.kind === 'maintenance' ? 'Dodano umowę utrzymania' : 'Dodano usługę');
      }
      setErrors({});
      await Promise.all([utils.services.invalidate(), utils.maintenance.invalidate()]);
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      if (appErrorCode(err) === 'VALIDATION_ERROR') {
        const fieldErrors = issuesToErrors(validationIssues(err), FIELD_KEYS);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
      }
      setFormError(
        appErrorCode(err) === 'CONFLICT'
          ? 'Ktoś zmienił tę umowę. Zamknij okno i spróbuj ponownie.'
          : errorMessage(err),
      );
    }
  };

  const pending = create.isPending || update.isPending;
  const maintenance = form.kind === 'maintenance';
  const clientAssets = assetsQuery.data ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      className={FULLSCREEN_ON_PHONE}
      title={
        editing
          ? maintenance
            ? 'Edytuj umowę utrzymania'
            : 'Edytuj usługę'
          : maintenance
            ? 'Nowa umowa utrzymania'
            : 'Nowa usługa'
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="service-form" loading={pending}>
            {editing ? 'Zapisz' : maintenance ? 'Dodaj umowę' : 'Dodaj usługę'}
          </Button>
        </>
      }
    >
      <form
        id="service-form"
        ref={formRef}
        onSubmit={submit}
        noValidate
        className="flex flex-col gap-4"
        aria-label="Umowa"
      >
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px]">
          <Field label="Nazwa" error={errors.name}>
            <Input
              autoFocus={!editing}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="np. Utrzymanie strony"
            />
          </Field>
          <Field label="Rodzaj" error={errors.kind}>
            <Select value={form.kind} onValueChange={(v) => set('kind', v as ServiceKind)} options={kindOptions} />
          </Field>
        </div>

        {seesRates && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Cena netto" error={errors.price}>
              <Input
                mono
                inputMode="decimal"
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
                placeholder="np. 1990"
              />
            </Field>
            <Field label="Waluta" error={errors.currency}>
              <Select
                value={form.currency}
                onValueChange={(v) => set('currency', v as Currency)}
                options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              />
            </Field>
            <Field label="Cykl" error={errors.cycle}>
              <Select
                value={form.cycle}
                onValueChange={(v) => set('cycle', v as BillingCycle)}
                options={BILLING_CYCLES.map((c) => ({ value: c, label: BILLING_CYCLE_LABELS[c] }))}
              />
            </Field>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Od" error={errors.periodStart}>
            <DateInput value={form.periodStart} onValueChange={(v) => set('periodStart', v)} />
          </Field>
          <Field label="Do (opcjonalnie)" error={errors.periodEnd}>
            <DateInput value={form.periodEnd} onValueChange={(v) => set('periodEnd', v)} />
          </Field>
          <Field label="Następna płatność" error={errors.nextPaymentDate}>
            <DateInput value={form.nextPaymentDate} onValueChange={(v) => set('nextPaymentDate', v)} />
          </Field>
        </div>

        <Field
          label="Ustalenia"
          error={errors.descriptionMd}
          hint="Zakres, czas reakcji, pula godzin. Możesz użyć Markdown."
        >
          <Textarea
            rows={4}
            value={form.descriptionMd}
            onChange={(e) => set('descriptionMd', e.target.value)}
            placeholder="Hosting, kopia zapasowa co tydzień, aktualizacje, do 1 h poprawek w miesiącu"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_140px]">
          <Field label="Warunki rozliczenia" error={errors.billingTerms}>
            <Input
              value={form.billingTerms}
              onChange={(e) => set('billingTerms', e.target.value)}
              placeholder="np. płatne z góry, faktura 7 dni"
            />
          </Field>
          <Field label="Pula godzin" error={errors.hoursPoolSeconds}>
            <Input
              mono
              inputMode="decimal"
              value={form.hours}
              onChange={(e) => set('hours', e.target.value)}
              placeholder="—"
            />
          </Field>
        </div>

        {editing && (
          <Field label="Status" error={errors.status}>
            <Select
              value={form.status}
              onValueChange={(v) => set('status', v as ServiceStatus)}
              options={SERVICE_STATUSES.map((s) => ({ value: s, label: SERVICE_STATUS_LABELS[s] }))}
            />
          </Field>
        )}

        {canAssets && clientAssets.length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <Label>Objęte zasoby</Label>
            {clientAssets.map((a) => (
              <Checkbox
                key={a.id}
                checked={form.assetIds.includes(a.id)}
                onCheckedChange={(v) =>
                  set('assetIds', v ? [...form.assetIds, a.id] : form.assetIds.filter((id) => id !== a.id))
                }
                label={a.name}
              />
            ))}
            {errors.assetIds && <p className="text-caption text-danger">{errors.assetIds}</p>}
          </fieldset>
        )}

        {maintenance && (
          <fieldset className="flex flex-col gap-2.5 border-t border-line pt-4">
            <Label>Przypomnienia</Label>
            {!editing && (
              <>
                <Checkbox
                  checked={form.remindOwner}
                  onCheckedChange={(v) => set('remindOwner', v)}
                  label="Przypomnij mi 30 i 7 dni przed"
                />
                <Checkbox
                  checked={form.remindClient}
                  onCheckedChange={(v) => set('remindClient', v)}
                  label="Przypomnij klientowi 30 dni przed (mail przez Akceptacje)"
                />
                <Checkbox
                  checked={form.remindOverdue}
                  onCheckedChange={(v) => set('remindOverdue', v)}
                  label="Po terminie: co 7 dni, najwyżej 3 razy"
                />
              </>
            )}
            <Checkbox
              checked={form.autoClientReminder}
              onCheckedChange={(v) => set('autoClientReminder', v)}
              disabled={!sendsEmails && !form.autoClientReminder}
              label="Wysyłaj przypomnienie do klienta automatycznie z zatwierdzonego szablonu"
            />
            <p className="text-caption text-fg-muted">
              {sendsEmails
                ? 'Na razie zapisujemy tylko ustawienie: mail do klienta i tak trafia do Akceptacji.'
                : 'Wymaga uprawnienia „Wysyła maile do klientów”.'}
            </p>
          </fieldset>
        )}

        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
